<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { Check } from 'lucide-vue-next'

import { mechanicVisuals } from '../mechanicVisuals'
import { CONTEST_MECHANICS } from '../utils'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type { ContestMechanic } from '@/types/global'
import type { MechanicVisualId } from '../mechanicVisuals'

const selected = defineModel<ContestMechanic>()

const { t } = useI18n()
const { send } = useEvent()
const { user } = useLocalStorage()
const notification = useNotification()

const options: (RadioCardOption & { id: MechanicVisualId })[] = [
  ...CONTEST_MECHANICS.map((id) => ({ id })),
  { id: 'adventCalendar' as const }
]

const isComingSoon = (id: MechanicVisualId): id is 'adventCalendar' => id === 'adventCalendar'

const waitlistByMerchant = useStorage<Record<string, boolean>>('contest-advent-calendar-waitlist-merchants', {})
const hasJoinedWaitlist = computed(() => !!waitlistByMerchant.value[user.value.merchant.id])

const handleSelect = (option: RadioCardOption) => {
  const mechanic = option.id as MechanicVisualId

  if (isComingSoon(mechanic)) return

  send('segment', 'contests.mechanic:clicked', { mechanic, usr_public_id: user.value.publicId })
  selected.value = mechanic
}

const joinWaitlist = () => {
  if (hasJoinedWaitlist.value) return

  waitlistByMerchant.value = { ...waitlistByMerchant.value, [user.value.merchant.id]: true }
  send('segment', 'contests.mechanic:waitlist:joined', {
    mechanic: 'adventCalendar',
    merchantId: user.value.merchant.id,
    usr_public_id: user.value.publicId
  })
  notification.create('success', {
    text: t('pages.contests.mechanics.adventCalendar.waitlist.success')
  })
}
</script>

<template>
  <div class="grid w-full auto-rows-fr grid-cols-2 gap-4 lg:grid-cols-3">
    <!-- `contents` drops the radio group's own grid so its cards become items of the grid above -->
    <AppRadioCard
      :model-value="{ id: selected }"
      :options="options"
      hide-radio
      variant="tile"
      class="contents"
      @update:model-value="handleSelect($event as RadioCardOption)"
    >
      <template v-for="(option, index) in options" :key="option.id" #[index]>
        <div class="flex h-full flex-col gap-1.5">
          <div
            class="relative mb-2 flex h-20 w-full items-center justify-center rounded-lg"
            :class="mechanicVisuals[option.id].panelClass"
          >
            <div class="flex size-11 items-center justify-center rounded-full bg-white/80 shadow-sm">
              <component
                :is="mechanicVisuals[option.id].icon"
                class="size-5"
                :class="mechanicVisuals[option.id].iconClass"
                :stroke-width="1.75"
              />
            </div>
            <Badge v-if="isComingSoon(option.id)" variant="neutral" class="absolute right-2 top-2">
              {{ t('pages.contests.mechanics.adventCalendar.comingSoon') }}
            </Badge>
          </div>

          <p class="text-sm font-medium text-foreground">{{ t(`pages.contests.mechanics.${option.id}.title`) }}</p>
          <p class="text-sm font-normal text-foreground-secondary">
            {{ t(`pages.contests.mechanics.${option.id}.description`) }}
          </p>

          <Button
            v-if="isComingSoon(option.id)"
            variant="outline"
            size="sm"
            class="mt-auto w-full"
            :disabled="hasJoinedWaitlist"
            @click.stop="joinWaitlist"
          >
            <Check v-if="hasJoinedWaitlist" />
            {{
              hasJoinedWaitlist
                ? t('pages.contests.mechanics.adventCalendar.waitlist.joined')
                : t('pages.contests.mechanics.adventCalendar.waitlist.cta')
            }}
          </Button>
        </div>
      </template>
    </AppRadioCard>
  </div>
</template>
